import type { ReactNode } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import TextLoading from '@/app/common/components/skeleton/TextLoading';

type OverviewSkeletonHeaderProps = {
  title: ReactNode;
  tooltip?: ReactNode;
};

type OverviewCardSkeletonProps = OverviewSkeletonHeaderProps & {
  items?: number;
};

const OverviewSkeletonHeader = ({
  title,
  tooltip
}: OverviewSkeletonHeaderProps) => (
  <FalconCardHeader
    title={
      <div>
        {title}
        {tooltip && <HelpTooltip html={tooltip} />}
      </div>
    }
    titleTag="h6"
    className="py-2"
    light
    endEl={
      <span className="text-600 opacity-50" aria-hidden="true">
        <FontAwesomeIcon icon="ellipsis-h" className="fs--2" />
      </span>
    }
  />
);

const LoadingRow = ({ index }: { index: number }) => (
  <div className="d-flex align-items-center gap-2">
    <TextLoading width={8} height={8} isWidthPx />
    <TextLoading width={index % 2 === 0 ? 52 : 42} height={10} />
    <TextLoading width={index % 2 === 0 ? 34 : 25} height={12} />
  </div>
);

export const OverviewCardSkeleton = ({
  title,
  tooltip,
  items = 4
}: OverviewCardSkeletonProps) => (
  <Card className="h-md-100" aria-busy="true" aria-label="درحال بارگذاری">
    <OverviewSkeletonHeader title={title} tooltip={tooltip} />
    <Card.Body className="py-3">
      <div className="d-flex flex-column gap-3 pt-2">
        {Array.from({ length: items }, (_, index) => (
          <LoadingRow key={index} index={index} />
        ))}
      </div>
    </Card.Body>
  </Card>
);

export const OverviewHealthCardSkeleton = ({
  title,
  tooltip
}: OverviewSkeletonHeaderProps) => (
  <Card className="h-md-100" aria-busy="true" aria-label="درحال بارگذاری">
    <OverviewSkeletonHeader title={title} tooltip={tooltip} />
    <Card.Body className="py-3">
      <Row className="justify-content-between g-0">
        <Col xs={5} sm={6} xxl className="pe-2 d-flex align-items-center">
          <TextLoading width={66} height={38} isWidthPx />
        </Col>
        <Col xs="auto" className="d-flex align-items-center">
          <TextLoading width={48} height={34} isWidthPx />
        </Col>
        <div className="mt-2 w-100 overflow-hidden rounded-pill">
          <TextLoading width={100} height={16} />
        </div>
      </Row>
    </Card.Body>
  </Card>
);

export const OverviewPieCardSkeleton = ({
  title,
  tooltip,
  items = 4
}: OverviewCardSkeletonProps) => (
  <Card className="h-md-100" aria-busy="true" aria-label="درحال بارگذاری">
    <OverviewSkeletonHeader title={title} tooltip={tooltip} />
    <Card.Body className="pt-2">
      <Row className="justify-content-between g-0">
        <Col xs={6} sm={6} xxl className="pe-2">
          <div className="d-flex flex-column gap-3 pt-2">
            {Array.from({ length: items }, (_, index) => (
              <LoadingRow key={index} index={index} />
            ))}
          </div>
        </Col>
        <Col
          xs={6}
          sm={6}
          className="ps-0 d-flex align-items-center justify-content-end"
        >
          <div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{
              width: '6.625rem',
              height: '6.625rem',
              border: '12px solid var(--falcon-gray-300)',
              borderTopColor: 'var(--falcon-gray-500)'
            }}
          >
            <TextLoading width={38} height={12} isWidthPx />
          </div>
        </Col>
      </Row>
    </Card.Body>
  </Card>
);
